import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useSelectedUser } from '../hooks/useSelectedUser';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { fmtDuration } from '../lib/formatters';
import { ChevronLeft, ChevronRight, Loader2, AlignLeft } from 'lucide-react';

// ─── helpers ──────────────────────────────────────────────────────────────────

const CATEGORY_COLORS = {
  'Work > Programming':        '#3b82f6',
  'Work > Dev Tools':          '#6366f1',
  'Work > Design':             '#8b5cf6',
  'Work > Documents':          '#a78bfa',
  'Work > Project Management': '#60a5fa',
  'Comms > Video Conferencing':'#10b981',
  'Comms > Messaging':         '#34d399',
  'Comms > Email':             '#6ee7b7',
  'Media > Video':             '#f59e0b',
  'Media > Music':             '#fbbf24',
  'Media > Games':             '#f97316',
  'Media > Social Media':      '#ef4444',
  'Other':                     '#9ca3af',
};

function catColor(category, isIdle) {
  if (isIdle) return '#e5e7eb';
  return CATEGORY_COLORS[category] || '#9ca3af';
}

function fmtDateStr(str) {
  const d = new Date(str + 'T12:00:00');
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}

function fmtTime(iso, tz = 'UTC', timeFormat = '12h') {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
    hour12: timeFormat === '12h',
  });
}

// Returns YYYY-MM-DD in the given timezone (defaults to local if tz not yet loaded)
function isoToDateStr(date, tz) {
  if (tz) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(date);
  }
  return date.toISOString().split('T')[0];
}

// Convert a session's startedAt/endedAt to % positions within a day (0–100)
// dayStart and dayEnd are epoch ms
function sessionToBar(session, dayStartMs, dayEndMs) {
  const spanMs = dayEndMs - dayStartMs;
  const start = Math.max(new Date(session.startedAt).getTime(), dayStartMs);
  const end = Math.min(new Date(session.endedAt).getTime(), dayEndMs);
  if (end <= start) return null;
  const left = ((start - dayStartMs) / spanMs) * 100;
  const width = ((end - start) / spanMs) * 100;
  return { left, width };
}

// Get the hour (0-23) of an epoch ms timestamp in a given IANA timezone
function hourInTz(epochMs, tz) {
  const str = new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone: tz }).format(epochMs);
  return parseInt(str, 10) % 24; // Intl returns 24 for midnight in some locales
}

// Get the start of a given date (YYYY-MM-DD) in epoch ms, anchored to midnight in tz
function dayStartInTz(dateStr, tz) {
  // Parse as midnight UTC, then find actual local midnight in tz
  // We use Intl to determine the UTC offset at that date in the target tz
  const utcMidnight = new Date(dateStr + 'T00:00:00Z').getTime();
  // Binary-search approach: format the UTC timestamp in tz and check if it reads midnight
  // Simpler: create a date string that's midnight in the tz
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false, timeZone: tz,
  }).formatToParts(utcMidnight);

  const get = (type) => parts.find(p => p.type === type)?.value;
  // Find UTC offset by comparing formatted local time vs UTC midnight
  const localStr = `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
  const localAsUtc = new Date(localStr + 'Z').getTime();
  const offsetMs = utcMidnight - localAsUtc; // positive = tz behind UTC, negative = ahead

  // Actual midnight in tz = utcMidnight + offsetMs... but offset may differ across DST
  // Most reliable: walk from UTC midnight by offset
  return utcMidnight + offsetMs;
}

// Build time axis labels every 2 hours, timezone-aware
function buildHourLabels(dayStartMs, dayEndMs, tz) {
  const labels = [];
  const spanMs = dayEndMs - dayStartMs;
  const TWO_HOURS = 2 * 3600 * 1000;

  // Start from first even hour at or after dayStartMs in the target tz
  let t = dayStartMs;
  const startHour = hourInTz(t, tz);
  // Round up to next even hour
  const nextEven = startHour % 2 === 0 ? startHour : startHour + 1;
  t += (nextEven - startHour) * 3600 * 1000;

  while (t <= dayEndMs) {
    const left = ((t - dayStartMs) / spanMs) * 100;
    const hour = hourInTz(t, tz);
    labels.push({ left, hour, epochMs: t });
    t += TWO_HOURS;
  }
  return labels;
}

function formatHour(h, timeFormat) {
  if (timeFormat === '24h') return `${String(h).padStart(2, '0')}:00`;
  if (h === 0) return '12am';
  if (h === 12) return '12pm';
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

// ─── Tooltip ─────────────────────────────────────────────────────────────────

function SessionTooltip({ session, tz, timeFormat }) {
  return (
    <div className="absolute z-20 bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 w-52 shadow-lg pointer-events-none">
      <p className="font-semibold truncate">{session.appName}</p>
      {session.windowTitle && (
        <p className="text-gray-300 truncate mt-0.5">{session.windowTitle}</p>
      )}
      {session.domain && (
        <p className="text-blue-300 truncate">{session.domain}</p>
      )}
      <p className="text-gray-400 mt-1">{session.category || 'Other'}</p>
      <p className="text-gray-300 mt-1">
        {fmtTime(session.startedAt, tz, timeFormat)} – {fmtTime(session.endedAt, tz, timeFormat)}
      </p>
      <p className="text-yellow-300 font-medium">{fmtDuration(session.durationSec)}</p>
      {session.hasTimerOverlap && (
        <p className="mt-1 flex items-center gap-1 text-green-300">
          <span className="h-2 w-2 rounded-full bg-green-400" />
          Timer active for {fmtDuration(session.trackedDurationSec)}
        </p>
      )}
      {/* Arrow */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
    </div>
  );
}

// ─── Timeline bar ─────────────────────────────────────────────────────────────

function TimelineBar({ sessions, dayStartMs, dayEndMs, tz, timeFormat }) {
  const [hovered, setHovered] = useState(null);

  return (
    <div className="relative h-10 bg-gray-100 rounded-lg overflow-visible">
      {sessions.map((session) => {
        const pos = sessionToBar(session, dayStartMs, dayEndMs);
        if (!pos || pos.width < 0.1) return null;
        const color = catColor(session.category, session.isIdle);
        return (
          <div
            key={session.id}
            className="absolute top-0 h-full rounded cursor-pointer transition-opacity hover:opacity-80"
            style={{
              left: `${pos.left}%`,
              width: `${Math.max(pos.width, 0.3)}%`,
              backgroundColor: color,
              boxShadow: session.hasTimerOverlap ? 'inset 0 0 0 1px rgba(34, 197, 94, 0.95)' : undefined,
            }}
            onMouseEnter={() => setHovered(session.id)}
            onMouseLeave={() => setHovered(null)}
          >
            {session.hasTimerOverlap && pos.width >= 1.5 && (
              <span className="absolute -top-1 right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-white" />
            )}
            {hovered === session.id && (
              <SessionTooltip session={session} tz={tz} timeFormat={timeFormat} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Session list row ────────────────────────────────────────────────────────

function SessionRow({ session, tz, timeFormat }) {
  const color = catColor(session.category, session.isIdle);
  return (
    <div className="flex items-center gap-4 py-2.5 border-b border-gray-100 last:border-0">
      <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <div className="w-36 text-sm text-gray-500 shrink-0">
        {fmtTime(session.startedAt, tz, timeFormat)} – {fmtTime(session.endedAt, tz, timeFormat)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">{session.appName}</p>
          {session.hasTimerOverlap && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700 shrink-0"
              title={`Timer active for ${fmtDuration(session.trackedDurationSec)}`}
            >
              <span className="h-2 w-2 rounded-full bg-green-500" />
              Timer on
            </span>
          )}
        </div>
        {session.windowTitle && (
          <p className="text-xs text-gray-400 truncate">{session.windowTitle}</p>
        )}
      </div>
      <div className="text-xs text-gray-400 shrink-0 hidden sm:block">{session.category || 'Other'}</div>
      <div className="text-sm text-gray-600 font-medium shrink-0 w-14 text-right">
        {fmtDuration(session.durationSec)}
      </div>
    </div>
  );
}

// ─── User picker ─────────────────────────────────────────────────────────────

function UserPicker({ users, selectedUserId, onSelect }) {
  if (!users?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {users.map((u) => (
        <button
          key={u.id}
          onClick={() => onSelect(u.id)}
          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
            selectedUserId === u.id
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {u.name}
        </button>
      ))}
    </div>
  );
}

// ─── Legend ──────────────────────────────────────────────────────────────────

const LEGEND_ITEMS = [
  { label: 'Work',  color: '#3b82f6' },
  { label: 'Comms', color: '#10b981' },
  { label: 'Media', color: '#f59e0b' },
  { label: 'Other', color: '#9ca3af' },
  { label: 'Idle',  color: '#e5e7eb', textClass: 'text-gray-400' },
  { label: 'Timer On', color: '#22c55e' },
];

function Legend() {
  return (
    <div className="flex flex-wrap gap-3">
      {LEGEND_ITEMS.map((item) => (
        <div key={item.label} className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: item.color }} />
          <span className={`text-xs ${item.textClass || 'text-gray-500'}`}>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Timeline() {
  const { user } = useAuth();
  const { timezone: tz, timeFormat } = useDisplaySettings();
  const [selectedUserId, setSelectedUserId] = useSelectedUser();
  const [searchParams, setSearchParams] = useSearchParams();

  // Date navigation — default to today in user's timezone
  const dateStr = searchParams.get('date') || isoToDateStr(new Date(), tz);

  function navigate(delta) {
    const d = new Date(dateStr + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + delta);
    setSearchParams((prev) => { prev.set('date', isoToDateStr(d, tz)); return prev; });
  }

  const isManager = user?.role === 'ADMIN' || user?.role === 'MANAGER';

  const { data: usersData } = useQuery({
    queryKey: ['users-list'],
    queryFn: () => api.get('/api/users').then((r) => r.data?.data || r.data),
    enabled: isManager,
  });
  const users = usersData || [];

  const targetUserId = isManager ? selectedUserId : user?.id;

  const { data: timelineData, isLoading, isError } = useQuery({
    queryKey: ['app-sessions-timeline', targetUserId, dateStr],
    queryFn: () =>
      api.get('/api/app-sessions/timeline', { params: { from: dateStr, to: dateStr, userId: targetUserId } })
        .then((r) => r.data?.data || []),
    enabled: !!targetUserId,
  });

  const sessions = timelineData || [];

  // Day bounds — midnight-to-midnight in user's timezone, compressed to actual data
  const dayBounds = useMemo(() => {
    const PADDING_MS = 30 * 60 * 1000;
    const tzToUse = tz || 'UTC';
    const dayStart = dayStartInTz(dateStr, tzToUse);
    const dayEnd = dayStart + 24 * 3600 * 1000 - 1;

    if (!sessions.length) {
      // No data — show 8am–6pm in user's tz
      return { startMs: dayStart + 8 * 3600 * 1000, endMs: dayStart + 18 * 3600 * 1000 };
    }
    const earliest = Math.min(...sessions.map(s => new Date(s.startedAt).getTime()));
    const latest = Math.max(...sessions.map(s => new Date(s.endedAt).getTime()));
    return {
      startMs: Math.max(earliest - PADDING_MS, dayStart),
      endMs: Math.min(latest + PADDING_MS, dayEnd),
    };
  }, [dateStr, sessions, tz]);

  const hourLabels = useMemo(
    () => buildHourLabels(dayBounds.startMs, dayBounds.endMs, tz || 'UTC'),
    [dayBounds, tz]
  );

  const totalActiveSec = sessions.filter((s) => !s.isIdle).reduce((a, s) => a + s.durationSec, 0);
  const totalTrackedSec = sessions.reduce((sum, s) => sum + (s.trackedDurationSec || 0), 0);
  // Filter sub-30s sessions from the list (still shown on timeline bar)
  const visibleSessions = sessions.filter((s) => s.durationSec >= 30);

  const isToday = dateStr === isoToDateStr(new Date(), tz);

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <AlignLeft className="w-5 h-5 text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Timeline</h1>
        </div>

        {/* Day navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-gray-700 min-w-44 text-center">
            {fmtDateStr(dateStr)}
          </span>
          <button
            onClick={() => navigate(1)}
            disabled={isToday}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* User picker */}
      {isManager && users.length > 0 && (
        <UserPicker users={users} selectedUserId={selectedUserId} onSelect={setSelectedUserId} />
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-600">
          Failed to load timeline data. Please try again.
        </div>
      )}

      {!isLoading && !isError && (
        <>
          {/* Summary + legend row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-gray-500">
              {sessions.length === 0
                ? 'No sessions recorded for this day'
                : `${sessions.length} sessions · ${fmtDuration(totalActiveSec)} active · ${fmtDuration(totalTrackedSec)} with timer on`}
            </p>
            <Legend />
          </div>

          {sessions.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-10 text-center text-sm text-gray-500">
              No activity recorded for this day. Make sure the Electron agent is running.
            </div>
          ) : (
            <>
              {/* Timeline chart */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                {/* Hour axis */}
                <div className="relative h-5 mb-2">
                  {hourLabels.map((label) => (
                    <span
                      key={label.hour}
                      className="absolute text-xs text-gray-400 -translate-x-1/2"
                      style={{ left: `${label.left}%` }}
                    >
                      {formatHour(label.hour, timeFormat)}
                    </span>
                  ))}
                </div>

                {/* Bar */}
                <TimelineBar
                  sessions={sessions}
                  dayStartMs={dayBounds.startMs}
                  dayEndMs={dayBounds.endMs}
                  tz={tz}
                  timeFormat={timeFormat}
                />

                {/* Tick lines */}
                <div className="relative h-1 mt-1">
                  {hourLabels.map((label) => (
                    <div
                      key={label.hour}
                      className="absolute top-0 w-px h-1.5 bg-gray-300"
                      style={{ left: `${label.left}%` }}
                    />
                  ))}
                </div>
              </div>

              {/* Session list */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-700 mb-3">Session Log</h2>
                <div>
                  {visibleSessions.map((session) => (
                    <SessionRow
                      key={session.id}
                      session={session}
                      tz={tz}
                      timeFormat={timeFormat}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
